import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { CustomFieldValidator } from '@backstage/plugin-scaffolder-react';

import { DAAS_PROJECT_TYPE } from '../../../types';
import {
  DAAS_NAME_MAX_LENGTH,
  NAMESPACE_MAX_LENGTH,
  NAMESPACE_MIN_LENGTH,
} from '../nameLimits';
import {
  ClusterConfiguration,
  DaasProjectInputData,
  DaasProjectInputUiOptions,
  SelfManagedCluster,
} from './types';

const NAMESPACE_PATTERN = '^[a-z0-9-*]+$';

const SUBJECT_MAX_LENGTH = 255;

const GITHUB_ORG = 'tomtom-internal';
const GITHUB_ORG_ID = '70519145';
const GITHUB_REPO = String.raw`[A-Za-z0-9._-]+`;
const GITHUB_SUBJECT_CLAIM = String.raw`[A-Za-z0-9._\-/:*@+]*[A-Za-z0-9._\-/*@+]`;

const GITHUB_SUBJECT_PATTERNS = [
  new RegExp(`^repo:${GITHUB_ORG}/${GITHUB_REPO}:${GITHUB_SUBJECT_CLAIM}$`),
  new RegExp(
    `^repo:${GITHUB_ORG}@${GITHUB_ORG_ID}/${GITHUB_REPO}@[0-9]+:${GITHUB_SUBJECT_CLAIM}$`,
  ),
];

// Azure DevOps workload identity federation subjects are Entra-signed and fully
// pinned except for the service connection id. Dex skips the audience check on
// token exchange, so the subject is the only gate: no wildcards are accepted.
// base64url of the TomTom Entra tenant 374f8026-7b54-4a3a-b87d-328fa26ec10d
const AZURE_TENANT = 'JoBPN1R7Okq4fTKPom7BDQ';
// base64url of the Azure DevOps first-party app 499b84ac-1321-427f-aa17-267ca6975798
const ADO_APP = 'rISbSSETf0KqFyZ8ppdXmA';
const ADO_ORG_ID = '6bd365a9-7e90-4df8-8786-c2f3fef91acf';
// Entra emits the service connection id lowercase and the subject is matched
// literally, so an uppercase id would be accepted here yet never match a token.
const ADO_SERVICE_CONNECTION_ID = String.raw`[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}`;

const ADO_SUBJECT_PREFIX = `/eid1/c/pub/t/${AZURE_TENANT}/a/${ADO_APP}/sc/${ADO_ORG_ID}/`;

const ADO_SUBJECT_PATTERNS = [
  new RegExp(`^${ADO_SUBJECT_PREFIX}${ADO_SERVICE_CONNECTION_ID}$`),
];

const CICD_SUBJECT_PATTERNS = [
  ...GITHUB_SUBJECT_PATTERNS,
  ...ADO_SUBJECT_PATTERNS,
];

export const validateUniqueNamespaces = (
  index: number,
  allNamespaces: string[],
): boolean => {
  const otherNamespaces = allNamespaces.filter((_, i) => i !== index);
  return !otherNamespaces.includes(allNamespaces[index].trim());
};

export const validateNamespacePattern = (namespace: string): boolean => {
  // Every rule is applied to the same trimmed value. Testing the pattern on the
  // trimmed string while measuring length and hyphens on the raw one accepted
  // `" a "`, `" -ab"` and `"ab- "`.
  const value = namespace.trim();
  if (value === '*') return true;
  if (!new RegExp(NAMESPACE_PATTERN).test(value)) return false;
  if (
    value.length < NAMESPACE_MIN_LENGTH ||
    value.length > NAMESPACE_MAX_LENGTH
  )
    return false;
  if (value.startsWith('-') || value.endsWith('-')) return false;
  return true;
};

export const validateUniqueSelfManagedClusters = (
  index: number,
  clusterId: string,
  allClusters: SelfManagedCluster[],
): boolean => {
  return !allClusters.some(
    (cluster, i) => i !== index && cluster?.cluster?.id === clusterId,
  );
};

export const projectNameFormatErrors = (projectName: string): string => {
  if (!projectName || !projectName.trim()) {
    return 'Project name is required';
  }
  if (!/^[a-z0-9-]+$/.test(projectName)) {
    return 'Project name must have only lowercase letters, numbers, and hyphens';
  }
  // The name becomes a Kubernetes namespace prefix and a Backstage entity name,
  // so it must be a DNS label: no leading or trailing hyphen.
  if (projectName.startsWith('-') || projectName.endsWith('-')) {
    return 'Project name must not start or end with a hyphen';
  }
  if (projectName.length > DAAS_NAME_MAX_LENGTH) {
    return `Project name must be at most ${DAAS_NAME_MAX_LENGTH} characters`;
  }
  return '';
};

export const projectNameInputErrors = async (
  projectName: string,
  catalogApi?: CatalogApi,
  fillEditFormData?: boolean,
): Promise<string> => {
  const formatError = projectNameFormatErrors(projectName);
  if (formatError) {
    return formatError;
  }

  if (catalogApi && !fillEditFormData) {
    try {
      const { items } = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.type': DAAS_PROJECT_TYPE,
          'metadata.name': projectName,
        },
        fields: ['metadata.name'],
      });
      if (items.length > 0) {
        return `Project "${projectName}" already exists in the catalog`;
      }
    } catch {
      // Skip uniqueness check if catalog is unreachable to avoid blocking users
    }
  }

  return '';
};

export const ownerInputErrors = (owner: string): string => {
  if (!owner || !owner.trim()) {
    return 'Owner is required';
  }
  return '';
};

export const securityGroupsInputErrors = (groups: string[]): string => {
  if (!groups || groups.length === 0) {
    return 'At least one security group is required';
  }
  return '';
};

export const duplicateCicdOidcSubjects = (subjects: string[]): Set<string> => {
  const trimmed = subjects
    .map(subject => (subject || '').trim())
    .filter(Boolean);
  return new Set(
    trimmed.filter((subject, index) => trimmed.indexOf(subject) !== index),
  );
};

export const validateCicdOidcSubject = (subject: string): boolean => {
  if (!subject || subject.length > SUBJECT_MAX_LENGTH) {
    return false;
  }
  return CICD_SUBJECT_PATTERNS.some(pattern => pattern.test(subject));
};

export const cicdOidcSubjectsInputErrors = (subjects?: string[]): string => {
  if (!subjects || subjects.length === 0) {
    return '';
  }

  const trimmed = subjects.map(subject => (subject || '').trim());

  if (trimmed.some(subject => !subject)) {
    return 'CI/CD OIDC subject claim cannot be empty';
  }
  if (new Set(trimmed).size !== trimmed.length) {
    return 'CI/CD OIDC subject claims must be unique';
  }
  if (subjects.some(subject => (subject || '').length > SUBJECT_MAX_LENGTH)) {
    return `CI/CD OIDC subject claim must be at most ${SUBJECT_MAX_LENGTH} characters`;
  }
  if (!subjects.every(validateCicdOidcSubject)) {
    return `CI/CD OIDC subject claims must be either a GitHub Actions claim (repo:tomtom-internal[@70519145]/REPO_NAME[@REPO_ID]:SUBJECT_CLAIM) or an Azure DevOps service connection claim (/eid1/c/pub/t/${AZURE_TENANT}/a/${ADO_APP}/sc/${ADO_ORG_ID}/SERVICE_CONNECTION_ID, lowercase)`;
  }
  return '';
};

/**
 * The namespace rules every cluster entry obeys, whichever kind it is. Both
 * lists are rendered into the same generated `values.yaml`, so they are
 * validated the same way.
 */
const namespaceListErrors = (
  namespaces: string[],
  options: { requireAtLeastOne?: boolean } = {},
): string => {
  if (options.requireAtLeastOne && namespaces.length === 0) {
    return 'At least one namespace must be specified';
  }
  for (const [namespaceIndex, namespace] of namespaces.entries()) {
    if (!namespace || !namespace.trim()) {
      return 'Namespace cannot be empty';
    }
    if (!validateNamespacePattern(namespace)) {
      return `Namespace must have only lowercase letters, numbers, and hyphens.
            Length must be between ${NAMESPACE_MIN_LENGTH} and ${NAMESPACE_MAX_LENGTH} characters, and cannot start or end with a hyphen.`;
    }
    if (!validateUniqueNamespaces(namespaceIndex, namespaces)) {
      return 'Namespace must be unique within the cluster';
    }
  }
  return '';
};

export const clusterInputErrors = (clusters: ClusterConfiguration): string => {
  // `||`, not `&&`: every branch below dereferences *both* arrays, so a
  // configuration carrying only one of the two keys - which is exactly what
  // `convertEntityToProject` produces from a partial `spec.clustersRawData` -
  // would pass an `&&` guard and then throw a TypeError.
  if (!clusters || !clusters.kaapClusters || !clusters.selfManagedClusters) {
    return 'Invalid cluster configuration data';
  }

  if (
    clusters.kaapClusters.length === 0 &&
    clusters.selfManagedClusters.length === 0
  ) {
    return 'At least one cluster must be specified';
  }

  if (clusters.kaapClusters.length !== 0) {
    for (const cluster of clusters.kaapClusters) {
      if (!cluster.tenant) {
        return 'Tenant must be specified';
      }
      if (cluster.tenant.pimEnabled && !cluster.tenant.pimGroupId) {
        return 'PIM access setup is not yet complete for this tenant. Please try again in a few minutes.';
      }
      // KaaP namespaces land in the same generated `values.yaml` as the
      // self-managed ones, so each entry gets the same pattern and uniqueness
      // rules rather than only a non-empty check. An *empty list* stays
      // allowed here, unlike for self-managed clusters: the KaaP -> DaaS
      // handoff prefills `namespaces: []`.
      const namespaceError = namespaceListErrors(cluster.namespaces ?? []);
      if (namespaceError) {
        return namespaceError;
      }
    }
  }

  if (clusters.selfManagedClusters.length !== 0) {
    for (const [
      clusterIndex,
      cluster,
    ] of clusters.selfManagedClusters.entries()) {
      const clusterId = cluster.cluster?.id;
      if (!clusterId) {
        return 'Cluster must be specified';
      }
      if (
        !validateUniqueSelfManagedClusters(
          clusterIndex,
          clusterId,
          clusters.selfManagedClusters,
        )
      ) {
        return 'Cluster must be unique';
      }

      const namespaceError = namespaceListErrors(cluster.namespaces ?? [], {
        requireAtLeastOne: true,
      });
      if (namespaceError) {
        return namespaceError;
      }
    }
  }

  return '';
};

export const notificationInputErrors = (
  notificationChannel: string,
  notificationServices?: string[],
): string => {
  const slackEnabled = (notificationServices || []).includes('slack');
  if (slackEnabled && (!notificationChannel || !notificationChannel.trim())) {
    return 'Slack channel is required when Slack notifications are enabled';
  }
  if (notificationChannel && notificationChannel.trim()) {
    if (!/^[a-z0-9-_]*$/.test(notificationChannel)) {
      return 'Notification channel must have only lowercase letters, numbers, hyphens, and underscores';
    }
  }
  return '';
};

export const daasProjectInputValidation: CustomFieldValidator<
  DaasProjectInputData,
  DaasProjectInputUiOptions
> = async (value, validation, context) => {
  // Basic field validation
  if (!value) {
    validation.addError('Project data is required');
    return;
  }

  // Project name validation (format + catalog uniqueness)
  const catalogApi = context?.apiHolder?.get(catalogApiRef);
  const fillEditFormData = context?.uiSchema?.['ui:options']?.fillEditFormData;
  const projectNameError = await projectNameInputErrors(
    value.projectName,
    catalogApi,
    fillEditFormData,
  );
  if (projectNameError) {
    validation.addError(projectNameError);
    return;
  }

  // Owner validation
  const ownerError = ownerInputErrors(value.owner);
  if (ownerError) {
    validation.addError(ownerError);
    return;
  }

  // Security groups validation
  const securityGroupsError = securityGroupsInputErrors(value.securityGroups);
  if (securityGroupsError) {
    validation.addError(securityGroupsError);
    return;
  }

  // CI/CD OIDC subjects (GitHub Actions, Azure DevOps)
  const cicdOidcSubjectsError = cicdOidcSubjectsInputErrors(
    value.cicdOidcSubjects,
  );
  if (cicdOidcSubjectsError) {
    validation.addError(cicdOidcSubjectsError);
    return;
  }

  // Cluster validation
  const clusterInputError = clusterInputErrors(value.clusters);
  if (clusterInputError) {
    validation.addError(clusterInputError);
    return;
  }

  // Notification channel
  const notificationError = notificationInputErrors(
    value.notificationChannel,
    value.notificationServices,
  );
  if (notificationError) {
    validation.addError(notificationError);
    return;
  }
};
